export function runSilentBashCommand(
  activeUser: string,
  command: string,
  isThickClient?: boolean,
): Promise<string> {
  return new Promise((resolve) => {
    const backendUrl =
      import.meta.env.VITE_TEST_FRESHERS_WS_TERMINAL_BASE_URL || "";
    if (!backendUrl) {
      resolve("");
      return;
    }
    const wsUrl = `${backendUrl}/bash-terminal?user=${encodeURIComponent(activeUser)}&tab=install-silent&isThickClient=${Boolean(isThickClient)}`;
    const socket = new WebSocket(wsUrl);
    let buffer = "";
    let isFinished = false;

    const marker = `__DONE_${Date.now()}_${Math.random().toString(36).slice(2, 8)}__`;

    const cleanUp = () => {
      try {
        socket.close();
      } catch {}
    };

    socket.addEventListener("open", () => {
      setTimeout(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(`${command}\necho ${marker}\n`);
        }
      }, 500);
    });

    socket.addEventListener("message", (event) => {
      const data = event.data as string;
      if (data === "__ping__") {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send("__pong__");
        }
        return;
      }
      buffer += data;
      if (buffer.includes(marker)) {
        isFinished = true;
        cleanUp();
        resolve(buffer);
      }
    });

    socket.addEventListener("error", () => {
      if (!isFinished) {
        isFinished = true;
        cleanUp();
        resolve(buffer);
      }
    });

    socket.addEventListener("close", () => {
      if (!isFinished) {
        isFinished = true;
        cleanUp();
        resolve(buffer);
      }
    });

    // Timeout safety net (1.5 seconds)
    setTimeout(() => {
      if (!isFinished) {
        isFinished = true;
        cleanUp();
        resolve(buffer);
      }
    }, 1500);
  });
}

export async function stopDockerDesktopSilently(
  activeUser: string,
): Promise<string> {
  const command = `powershell.exe -Command "Stop-Process -Name 'Docker Desktop' -Force"`;
  try {
    return await runSilentBashCommand(activeUser, command);
  } catch (err) {
    console.error("Failed to stop Docker Desktop:", err);
    return "";
  }
}

const DEVOPS_ACTIVE_PORTS_KEY = "devops_active_ports";

export const DEFAULT_DEVOPS_PORTS: Record<string, number> = {
  jenkins: 8080,
  grafana: 3000,
  prometheus: 9090,
  vscode: 8443,
};

export function parseHostPort(output: string): number | null {
  if (!output) return null;
  const clean = output.replace(/\r/g, "").trim();
  const matches = [...clean.matchAll(/:(\d+)/g)];
  if (matches.length > 0) {
    for (let i = matches.length - 1; i >= 0; i--) {
      const port = parseInt(matches[i][1], 10);
      if (!isNaN(port) && port > 0) return port;
    }
  }
  return null;
}

export const SERVICE_CONTAINER_MAP: Record<
  string,
  { container: string; toolId: string; defaultPort: number }
> = {
  jenkins: { container: "jenkins", toolId: "jenkins", defaultPort: 8080 },
  grafana: { container: "grafana", toolId: "grafana", defaultPort: 3000 },
  prometheus: {
    container: "prometheus",
    toolId: "prometheus",
    defaultPort: 9090,
  },
  "code editor": {
    container: "vscode-server",
    toolId: "vscode",
    defaultPort: 8443,
  },
  vscode: { container: "vscode-server", toolId: "vscode", defaultPort: 8443 },
  "vscode-server": {
    container: "vscode-server",
    toolId: "vscode",
    defaultPort: 8443,
  },
};

export function getDevOpsActivePort(
  toolId: string,
  fallbackPort?: number,
): number {
  const normId =
    toolId === "code editor" || toolId === "vscode-server" ? "vscode" : toolId;
  try {
    const raw = localStorage.getItem(DEVOPS_ACTIVE_PORTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed[normId] === "number") {
        return parsed[normId];
      }
    }
  } catch (_) {}
  return (
    fallbackPort ??
    DEFAULT_DEVOPS_PORTS[normId] ??
    (normId === "vscode" ? 8443 : 8080)
  );
}

export function setDevOpsActivePort(toolId: string, port: number): void {
  const normId =
    toolId === "code editor" || toolId === "vscode-server" ? "vscode" : toolId;
  try {
    const raw = localStorage.getItem(DEVOPS_ACTIVE_PORTS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[normId] = port;
    localStorage.setItem(DEVOPS_ACTIVE_PORTS_KEY, JSON.stringify(parsed));
  } catch (_) {}
}

export async function detectAndSyncDevOpsPort(
  activeUser: string,
  serviceName: string,
): Promise<number | null> {
  const norm = serviceName.trim().toLowerCase();
  const mapping = SERVICE_CONTAINER_MAP[norm];
  if (!mapping) return null;

  try {
    const cmd = `docker port ${mapping.container} 2>/dev/null || true`;
    const out = await runSilentBashCommand(activeUser, cmd);
    const detected = parseHostPort(out);
    if (detected) {
      setDevOpsActivePort(mapping.toolId, detected);
      return detected;
    }
  } catch (err) {
    console.error("Failed to detect port for", serviceName, err);
  }
  return null;
}

export function getDevOpsServiceUrl(
  serviceName: string,
  portOverride?: number,
): string {
  const norm = serviceName.trim().toLowerCase();
  if (norm === "jenkins") {
    const port = portOverride ?? getDevOpsActivePort("jenkins", 8080);
    return import.meta.env.VITE_JENKINS_URL || `http://localhost:${port}`;
  }
  if (norm === "grafana") {
    const port = portOverride ?? getDevOpsActivePort("grafana", 3000);
    return import.meta.env.VITE_GRAFANA_URL || `http://localhost:${port}`;
  }
  if (norm === "prometheus") {
    const port = portOverride ?? getDevOpsActivePort("prometheus", 9090);
    return import.meta.env.VITE_PROMETHEUS_URL || `http://localhost:${port}`;
  }
  if (norm === "code editor" || norm === "vscode" || norm === "vscode-server") {
    const port = portOverride ?? getDevOpsActivePort("vscode", 8443);
    return import.meta.env.VITE_VSCODE_URL || `http://localhost:${port}`;
  }
  return import.meta.env.VITE_DEVOPS_COMMON_URL || "http://localhost:5173";
}

export async function cleanupDevOpsSession(
  user?: string,
  testId?: string,
  questionId?: string,
): Promise<boolean> {
  try {
    const baseUrl =
      import.meta.env.VITE_TEST_FRESHERS_WS_TERMINAL_BASE_URL ||
      "http://localhost:8092";
    const cleanBase = baseUrl.replace(/^wss?:\/\//, "http://");
    const params = new URLSearchParams();
    if (user) params.set("user", user);
    if (testId) params.set("testId", testId);
    if (questionId) params.set("questionId", questionId);

    const res = await fetch(`${cleanBase}/api/devops/cleanup?${params.toString()}`, {
      method: "POST",
    });
    return res.ok;
  } catch (e) {
    console.warn("Failed to trigger DevOps session cleanup:", e);
    return false;
  }
}
